import { GenericModelController } from "@/controllers/model-controllers/abstract/generic-model-controller"
import type { IRentalDispenseMaterialization } from "@/interfaces/api/rental-dispense-materialization.interface"
import { RentalDispenseMaterialization } from "@/models/rental-dispense-materialization.model"
import { RentalDispenseMaterializationRepository } from "@/repositories/rental-dispense-materialization-repository"
import { useRentalDispenseMaterializationStore } from "@/store/rental-dispense-materialization.store"
import { storeToRefs } from "pinia"
import { ODataQueryBuilder } from "@/builder/odata-query-builder"

export class RentalDispenseMaterializationController extends GenericModelController<
  RentalDispenseMaterialization,
  IRentalDispenseMaterialization,
  RentalDispenseMaterializationRepository
> {
  constructor() {
    const store = useRentalDispenseMaterializationStore()
    const { rentalDispenseMaterializations } = storeToRefs(store)

    super(
      RentalDispenseMaterialization,
      { collection: rentalDispenseMaterializations },
      new RentalDispenseMaterializationRepository()
    )
  }

  /**
   * Retrieves the materializations for the supplied ids.
   *
   * Ids that are already in the store are left out of the query, so the server only
   * serializes what this client is missing. Those local instances are still returned,
   * making the result cover every requested id regardless of where it came from. When
   * nothing is missing, no request is made at all.
   */
  public async retrieveByIds(ids: string[]) {
    const requestedIds = new Set(ids)
    if (requestedIds.size === 0) {
      return []
    }

    const store = useRentalDispenseMaterializationStore()
    const presentMaterializations = store.rentalDispenseMaterializations.filter(
      (materialization) => materialization.id != null && requestedIds.has(materialization.id)
    )

    for (const materialization of presentMaterializations) {
      requestedIds.delete(materialization.id!)
    }

    if (requestedIds.size === 0) {
      return presentMaterializations
    }

    const retrievedMaterializations = await this.getList({
      query: new ODataQueryBuilder().in("id", [...requestedIds])
    })

    return [...presentMaterializations, ...retrievedMaterializations]
  }
}
